import { Bell } from '@phosphor-icons/react';
import {
  getListNotificationsQueryKey,
  useGetNotificationUnreadCount,
  useListNotifications,
} from '@reelplicate/api-client/notifications';
import { useListWorkspaces } from '@reelplicate/api-client/workspaces';
import {
  Link,
  NavItem,
  NotificationCenter,
  type NotificationEntry,
} from '@reelplicate/ui';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router';

import { toEntry } from './to-entry';
import { useNotificationActions } from './use-notification-actions';

/** How often the bell checks for new notifications. */
export const UNREAD_POLL_MS = 45_000;

/**
 * The sidebar's "Notifications" item (Figma "Screen — Home · Exploration B",
 * 343:1771): a Nav Item with the unread count that opens the Notification
 * Center to its right. The count is polled; the list loads while the panel is
 * open. Opening a notification (or its action) marks it read, closes the
 * panel and goes to the thing it's about; "View all notifications" opens the
 * full page.
 */
export function NotificationsNavItem() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const { data: workspaces } = useListWorkspaces();
  const workspaceId = workspaces?.[0]?.id ?? '';
  const enabled = Boolean(workspaceId);

  const { data: count } = useGetNotificationUnreadCount(workspaceId, {
    query: { enabled, refetchInterval: UNREAD_POLL_MS, throwOnError: false },
  });
  const { data: page } = useListNotifications(workspaceId, undefined, {
    query: { enabled: enabled && open, throwOnError: false },
  });
  const { markOne, markAll } = useNotificationActions(workspaceId);

  // Something new arrived while the list was cached: fetch it again.
  const unreadAll = count?.all;
  useEffect(() => {
    if (unreadAll === undefined) return;
    void queryClient.invalidateQueries({
      queryKey: getListNotificationsQueryKey(workspaceId),
    });
  }, [unreadAll, workspaceId, queryClient]);

  /** Mark read and close; the caller (or the link itself) does the navigating. */
  const dismiss = (notificationId: string) => {
    setOpen(false);
    const target = page?.items.find((item) => item.id === notificationId);
    if (target) markOne(target);
  };

  const now = new Date();
  const entries: NotificationEntry[] = (page?.items ?? []).map((notification) => ({
    ...toEntry(notification, now),
    action: notification.action && (
      <Link asChild size="sm">
        <RouterLink
          to={notification.action.path}
          onClick={() => dismiss(notification.id)}
        >
          {notification.action.label}
        </RouterLink>
      </Link>
    ),
  }));

  return (
    <NotificationCenter
      open={open}
      onOpenChange={setOpen}
      side="right"
      align="start"
      notifications={entries}
      unreadCount={count?.all}
      onMarkAllRead={() => {
        const newest = page?.items[0]?.occurredAt;
        if (newest) markAll(newest);
      }}
      onNotificationSelect={({ id }) => {
        const target = page?.items.find((item) => item.id === id);
        if (!target) return;
        dismiss(id);
        void navigate(target.targetPath);
      }}
      viewAllHref="/notifications"
      onViewAll={() => {
        setOpen(false);
        void navigate('/notifications');
      }}
      trigger={
        <NavItem asChild icon={Bell} count={count?.all || undefined}>
          <button type="button">Notifications</button>
        </NavItem>
      }
    />
  );
}
